import React, { useState, useMemo, useEffect } from 'react';
import { MdOutlineMarkEmailRead } from 'react-icons/md';
import useToastWithSound from '../../hooks/useToastWithSound';
import api from '../../ui/api';
import useData from '../useData';
import { useAuth } from '../auth';
import useDirtyForm from '../useDirtyForm';
import PageShell, { Button } from '../../ui/PageShell';
import Card, { EmptyState } from '../../ui/Card';
import Table from '../../ui/Table';
import Tabs from '../../ui/Tabs';
import NotificationCompose, {
  emptyCompose, describeAudience, typeLabel, typeTone,
} from './NotificationCompose';
import NotificationDetail from './NotificationDetail';
import HelpButton from '../help/HelpButton';
import './notifications.css';

/**
 * Notifications — two tabs over one idea.
 *
 *   السجل   every send this school has made, newest first, with its
 *           numbers; click a row to see exactly who got it and who
 *           opened it.
 *   الوارد  this user's own inbox (unchanged from before).
 *
 * The history tab is the part v1 was missing in practice: it HAD a
 * SentNotifications screen with these very stats, but nothing linked
 * to it, so in effect no one could ever check what had been sent. It
 * is the first tab here, not a hidden page, because "what did we tell
 * the parents last week" is asked far more often than anything else on
 * this screen.
 *
 * A teacher gets the same screen, scoped by the SERVER: their own
 * sends, plus only their classes' share of anyone else's send (counts
 * recounted, audience reduced to their sections, sender shown as
 * «الإدارة»). They can only compose to their own sections/students.
 */

const fmt = (iso) => (iso ? String(iso).slice(0, 16).replace('T', ' ') : '—');

export default function Notifications() {
  const toast = useToastWithSound();
  const { isTeacher } = useAuth();
  const [tab, setTab] = useState('sent');

  // --- history (the batch log) ---------------------------------------
  const { data: batches, loading: batchesLoading, error: batchesError, reload: reloadBatches } =
    useData(() => api.notificationBatches({ limit: 100 }), []);

  const [detailFor, setDetailFor] = useState(null);
  const { data: recipients, loading: recipientsLoading } = useData(
    async () => {
      if (!detailFor) return null;
      return api.notificationBatch(detailFor.id);
    },
    [detailFor]
  );

  // --- inbox ----------------------------------------------------------
  const { data: inbox, loading: inboxLoading, reload: reloadInbox } =
    useData(() => api.notifications({ limit: 50 }), []);

  // --- reference data the compose dialog needs -------------------------
  // For a teacher every list is already cut to their classes by the API
  // (sections/students); grades are narrowed here to the ones those
  // sections belong to, and the staff list is not needed at all.
  const { data: refData } = useData(async () => {
    const [students, sections, grades, teachers] = await Promise.all([
      api.students(), api.sections(), api.grades(), isTeacher ? [] : api.teachers(),
    ]);
    const gradeIds = new Set(sections.map((s) => String(s.grade_id)));
    return {
      students,
      sections,
      grades: isTeacher ? grades.filter((g) => gradeIds.has(String(g.id))) : grades,
      teachers,
    };
  }, [isTeacher]);

  // --- compose ----------------------------------------------------------
  const [composing, setComposing] = useState(false);
  const [form, setForm] = useState(emptyCompose);
  const [sending, setSending] = useState(false);
  const dirty = useDirtyForm(form);

  // "How many would this reach" — asked of the server (the same query
  // the send runs), debounced so typing the message doesn't spam it.
  const [estimate, setEstimate] = useState(null);
  const audienceKey = JSON.stringify([form.kind, form.grade_ids, form.section_ids, form.student_ids,
    form.teacher_ids, form.all_teachers, form.notify_parents]);
  useEffect(() => {
    if (!composing) return undefined;
    setEstimate(null);
    // Nobody picked yet: nothing to count (the server answers 400).
    const f = form;
    if ((f.kind === 'grades' && !f.grade_ids.length) || (f.kind === 'sections' && !f.section_ids.length)
      || (f.kind === 'students' && !f.student_ids.length)
      || (f.kind === 'teachers' && !f.all_teachers && !f.teacher_ids.length)) return undefined;
    let cancelled = false;
    const t = setTimeout(() => {
      api.estimateAudience(form)
        .then((r) => { if (!cancelled) setEstimate(r); })
        .catch(() => { if (!cancelled) setEstimate(null); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composing, audienceKey]);

  const openCompose = () => {
    const fresh = emptyCompose();
    setForm(fresh);
    dirty.reset(fresh);
    setComposing(true);
  };

  const send = async () => {
    // Guard the double-click: a second submit while the first is in
    // flight would create a second batch and notify every parent twice.
    if (sending) return;
    setSending(true);
    try {
      const result = await api.sendNotification(form);
      toast.success(`تم الإرسال إلى ${result.recipient_count} مستلم`);
      setComposing(false);
      reloadBatches();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const markAllRead = async () => {
    try { await api.markAllRead(); toast.success('تم تعليم الكل كمقروء'); reloadInbox(); }
    catch (err) { toast.error(err.message); }
  };

  // --- history table ----------------------------------------------------
  const batchRows = useMemo(
    () => (batches || []).map((b) => ({
      ...b,
      audience_text: describeAudience(b.audience, refData),
      type_text: typeLabel(b.type),
      sender: b.source === 'system' ? 'تلقائي' : (isTeacher && b.is_mine ? 'أنا' : (b.created_by_name || '—')),
      read_pct: b.recipient_count ? Math.round((b.read_count / b.recipient_count) * 100) : 0,
    })),
    [batches, refData, isTeacher]
  );

  const columns = [
    { key: 'created_at', label: 'التاريخ', nowrap: true, formatValue: fmt },
    { key: 'title', label: 'العنوان', width: 2, clamp: 1 },
    { key: 'type_text', label: 'النوع', nowrap: true, badge: (v, row) => typeTone(row.type) },
    { key: 'audience_text', label: 'الجهة', width: 2 },
    { key: 'recipient_count', label: 'مستلم', nowrap: true, align: 'center' },
    { key: 'device_count', label: 'جهاز', nowrap: true, align: 'center' },
    {
      key: 'read_pct',
      label: 'قرأ',
      nowrap: true,
      align: 'center',
      render: (row) => (
        <span className="notif-readcell" title={`${row.read_count} من ${row.recipient_count}`}>
          <span className="notif-readbar"><i style={{ width: `${row.read_pct}%` }} /></span>
          <span className="notif-readnum">{row.read_count}</span>
        </span>
      ),
      exportValue: (row) => `${row.read_count}/${row.recipient_count}`,
    },
    {
      // `sender` is precomputed on the row rather than derived here from
      // `row.source`: Table's filter dropdown calls formatValue(v) with
      // ONE argument (Table.js:252), so any column whose text depends on
      // the whole row crashes the moment that column gets a filter.
      key: 'sender',
      label: 'المُرسل',
      nowrap: true,
      badge: (v, row) => (row?.source === 'system' ? 'info' : 'neutral'),
    },
  ];

  return (
    <PageShell
      title="الإشعارات"
      description="إرسال الإشعارات وسجل ما تم إرساله"
      actions={
        <>
          {tab === 'inbox' && (
            <Button variant="secondary" onClick={markAllRead}>
              <MdOutlineMarkEmailRead size={16} style={{ verticalAlign: '-3px' }} /> تعليم الكل كمقروء
            </Button>
          )}
          <HelpButton guides={['notify-send']} />
          <Button onClick={openCompose} data-guide="notif-add">+ إشعار جديد</Button>
        </>
      }
    >
      <Tabs
        active={tab}
        onChange={setTab}
        items={[
          { id: 'sent', label: 'السجل', badge: (batches || []).length },
          { id: 'inbox', label: 'الوارد', badge: (inbox || []).filter((n) => !n.is_read).length || null },
        ]}
      />

      {tab === 'sent' ? (
        <>
          {batchesError && <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{batchesError}</div></div>}
          <Card padded={false}>
            <Table
              columns={columns}
              rows={batchRows}
              keyField="id"
              loading={batchesLoading}
              emptyText="لم يُرسل أي إشعار بعد"
              searchPlaceholder="ابحث في العناوين..."
              searchableKeys={['title', 'message', 'audience_text', 'sender']}
              renderActions={(row) => (
                <button type="button" className="ui-icon-btn" title="التفاصيل" onClick={() => setDetailFor(row)}>
                  ⤢
                </button>
              )}
              actionsLabel="تفاصيل"
              exportName="notifications-log"
            />
          </Card>
        </>
      ) : (
        <Card padded={false}>
          {inboxLoading ? (
            <p style={{ padding: 'var(--ui-space-4)', margin: 0 }}>جارٍ التحميل...</p>
          ) : (inbox || []).length === 0 ? (
            <EmptyState icon="🔔" title="لا توجد إشعارات" />
          ) : (
            <ul className="notif-list">
              {inbox.map((n) => (
                <li key={n.id} className={`notif-row${n.is_read ? '' : ' is-unread'}`}>
                  <div className="notif-main">
                    <strong>{n.title}</strong>
                    <p>{n.message}</p>
                  </div>
                  <span className={`ui-badge ui-badge-${typeTone(n.type)}`}>{typeLabel(n.type)}</span>
                  <span className="notif-date">{fmt(n.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {composing && (
        <NotificationCompose
          form={form}
          setForm={setForm}
          refData={refData}
          isTeacher={isTeacher}
          dirty={dirty}
          estimate={estimate}
          sending={sending}
          onSend={send}
          onClose={() => setComposing(false)}
        />
      )}

      {detailFor && (
        <NotificationDetail
          batch={detailFor}
          recipients={recipients}
          loading={recipientsLoading}
          refData={refData}
          onClose={() => setDetailFor(null)}
        />
      )}
    </PageShell>
  );
}
